import { Check, Tag } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Separator } from '@reflex/ui/components/ui/separator';
import { timeAgo, formatDateTime } from '@reflex/ui/lib/format';
import { cn } from '@reflex/ui/lib/utils';
import { NotificationBody } from './renderers';
import { ReleaseTypeBadge } from './ReleaseTypeBadge';
import type { Notification } from '../../shared/types.js';

export interface ReleaseGroupContentProps {
  /** Every un-dismissed release in the group, newest first. */
  releases: Notification[];
  onDismissAll?: () => void;
  isDismissing?: boolean;
  className?: string;
}

/**
 * Detail-view counterpart of {@link ReleaseGroupInboxRow}: when the user
 * opens a release row that bundles 2+ un-dismissed releases, render every
 * release's full body in one scroll so they can catch up on the stack
 * without round-tripping back to the inbox, and surface a single
 * "Dismiss all" button that clears the entire group.
 */
export function ReleaseGroupContent({
  releases,
  onDismissAll,
  isDismissing,
  className,
}: ReleaseGroupContentProps) {
  if (releases.length === 0) return null;
  const latest = releases[0];

  return (
    <div className={cn('w-full max-w-2xl', className)} data-testid="release-group-content">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border/50 bg-muted/40 text-muted-foreground">
            <Tag className="h-5 w-5" />
          </div>
          <div className="flex flex-col gap-0.5">
            <Badge variant="outline" className="w-fit text-3xs">
              {releases.length} New Releases
            </Badge>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>Latest {timeAgo(latest.createdAt)}</span>
              <span className="text-border">·</span>
              <time dateTime={new Date(latest.createdAt).toISOString()}>
                {formatDateTime(latest.createdAt)}
              </time>
            </div>
          </div>
        </div>

        <div className="shrink-0">
          {onDismissAll && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDismissAll}
              disabled={isDismissing}
              data-testid="release-group-dismiss-all-button"
            >
              <Check className="icon-sm" aria-hidden="true" />
              Dismiss all
            </Button>
          )}
        </div>
      </div>

      <Separator className="my-5" />

      <div className="flex flex-col gap-8">
        {releases.map((release, idx) => (
          <section key={release.id} data-testid={`release-group-item-${release.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-2">
                <h2 className="text-xl font-semibold tracking-tight">{release.title}</h2>
                <div className="flex flex-wrap items-center gap-2">
                  {release.payload.type === 'release_event' && (
                    <ReleaseTypeBadge releaseType={release.payload.releaseType} />
                  )}
                  {idx === 0 && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                      Latest
                    </span>
                  )}
                </div>
              </div>
              <span className="shrink-0 pt-1 text-3xs text-muted-foreground">
                {timeAgo(release.createdAt)}
              </span>
            </div>
            <div className="mt-4">
              <NotificationBody notification={release} />
            </div>
            {idx < releases.length - 1 && <Separator className="mt-8" />}
          </section>
        ))}
      </div>
    </div>
  );
}
